/**
 * Color Scheme Definitions
 * Predefined color palettes for the application
 */

export const COLOR_SCHEMES = {
  DEFAULT: 'default',
  OCEAN: 'ocean',
  FOREST: 'forest',
  SUNSET: 'sunset',
  ROYAL: 'royal',
};

/**
 * Color scheme configurations
 * Each scheme has light and dark variants with full color palette
 */
export const colorSchemes = {
  [COLOR_SCHEMES.DEFAULT]: {
    id: 'default',
    name: 'Default',
    description: 'Clean blue theme',
    preview: {
      primary: '#3B82F6',
      secondary: '#6B7280',
      accent: '#8B5CF6',
    },
    light: {
      background: '0 0% 100%',
      foreground: '222.2 84% 4.9%',
      card: '0 0% 100%',
      cardForeground: '222.2 84% 4.9%',
      popover: '0 0% 100%',
      popoverForeground: '222.2 84% 4.9%',
      primary: '221.2 83.2% 53.3%',
      primaryForeground: '210 40% 98%',
      secondary: '210 40% 96.1%',
      secondaryForeground: '222.2 47.4% 11.2%',
      muted: '210 40% 96.1%',
      mutedForeground: '215.4 16.3% 46.9%',
      accent: '210 40% 96.1%',
      accentForeground: '222.2 47.4% 11.2%',
      destructive: '0 84.2% 60.2%',
      destructiveForeground: '210 40% 98%',
      border: '214.3 31.8% 91.4%',
      input: '214.3 31.8% 91.4%',
      ring: '221.2 83.2% 53.3%',
      // Sidebar specific colors
      sidebarBackground: '0 0% 98%',
      sidebarForeground: '240 5.3% 26.1%',
      sidebarPrimary: '221.2 83.2% 53.3%',
      sidebarPrimaryForeground: '0 0% 100%',
      sidebarAccent: '240 4.8% 95.9%',
      sidebarAccentForeground: '240 5.9% 10%',
      sidebarBorder: '220 13% 91%',
      sidebarRing: '221.2 83.2% 53.3%',
    },
    dark: {
      background: '222.2 84% 4.9%',
      foreground: '210 40% 98%',
      card: '222.2 84% 4.9%',
      cardForeground: '210 40% 98%',
      popover: '222.2 84% 4.9%',
      popoverForeground: '210 40% 98%',
      primary: '217.2 91.2% 59.8%',
      primaryForeground: '222.2 47.4% 11.2%',
      secondary: '217.2 32.6% 17.5%',
      secondaryForeground: '210 40% 98%',
      muted: '217.2 32.6% 17.5%',
      mutedForeground: '215 20.2% 65.1%',
      accent: '217.2 32.6% 17.5%',
      accentForeground: '210 40% 98%',
      destructive: '0 62.8% 30.6%',
      destructiveForeground: '210 40% 98%',
      border: '217.2 32.6% 17.5%',
      input: '217.2 32.6% 17.5%',
      ring: '224.3 76.3% 48%',
      // Sidebar specific colors
      sidebarBackground: '240 5.9% 10%',
      sidebarForeground: '240 4.8% 95.9%',
      sidebarPrimary: '224.3 76.3% 48%',
      sidebarPrimaryForeground: '0 0% 100%',
      sidebarAccent: '240 3.7% 15.9%',
      sidebarAccentForeground: '240 4.8% 95.9%',
      sidebarBorder: '240 3.7% 15.9%',
      sidebarRing: '217.2 91.2% 59.8%',
    },
  },

  [COLOR_SCHEMES.OCEAN]: {
    id: 'ocean',
    name: 'Ocean',
    description: 'Cool cyan and teal tones',
    preview: {
      primary: '#06B6D4',
      secondary: '#64748B',
      accent: '#14B8A6',
    },
    light: {
      background: '0 0% 100%',
      foreground: '200 50% 10%',
      card: '0 0% 100%',
      cardForeground: '200 50% 10%',
      popover: '0 0% 100%',
      popoverForeground: '200 50% 10%',
      primary: '189 94% 43%',
      primaryForeground: '0 0% 100%',
      secondary: '199 18% 46%',
      secondaryForeground: '0 0% 100%',
      muted: '199 18% 95%',
      mutedForeground: '199 18% 40%',
      accent: '173 80% 40%',
      accentForeground: '0 0% 100%',
      destructive: '0 84.2% 60.2%',
      destructiveForeground: '0 0% 100%',
      border: '199 18% 90%',
      input: '199 18% 90%',
      ring: '189 94% 43%',
      sidebarBackground: '199 25% 97%',
      sidebarForeground: '200 50% 20%',
      sidebarPrimary: '189 94% 43%',
      sidebarPrimaryForeground: '0 0% 100%',
      sidebarAccent: '199 25% 93%',
      sidebarAccentForeground: '200 50% 15%',
      sidebarBorder: '199 18% 88%',
      sidebarRing: '189 94% 43%',
    },
    dark: {
      background: '200 50% 5%',
      foreground: '199 25% 95%',
      card: '200 40% 8%',
      cardForeground: '199 25% 95%',
      popover: '200 40% 8%',
      popoverForeground: '199 25% 95%',
      primary: '189 94% 50%',
      primaryForeground: '200 50% 5%',
      secondary: '199 18% 30%',
      secondaryForeground: '199 25% 95%',
      muted: '200 30% 15%',
      mutedForeground: '199 18% 60%',
      accent: '173 80% 45%',
      accentForeground: '200 50% 5%',
      destructive: '0 62.8% 35%',
      destructiveForeground: '199 25% 95%',
      border: '200 30% 18%',
      input: '200 30% 18%',
      ring: '189 94% 50%',
      sidebarBackground: '200 45% 7%',
      sidebarForeground: '199 25% 90%',
      sidebarPrimary: '189 94% 50%',
      sidebarPrimaryForeground: '200 50% 5%',
      sidebarAccent: '200 30% 12%',
      sidebarAccentForeground: '199 25% 90%',
      sidebarBorder: '200 30% 15%',
      sidebarRing: '189 94% 50%',
    },
  },

  [COLOR_SCHEMES.FOREST]: {
    id: 'forest',
    name: 'Forest',
    description: 'Natural green tones',
    preview: {
      primary: '#10B981',
      secondary: '#78716C',
      accent: '#84CC16',
    },
    light: {
      background: '0 0% 100%',
      foreground: '150 30% 10%',
      card: '0 0% 100%',
      cardForeground: '150 30% 10%',
      popover: '0 0% 100%',
      popoverForeground: '150 30% 10%',
      primary: '160 84% 39%',
      primaryForeground: '0 0% 100%',
      secondary: '25 5% 45%',
      secondaryForeground: '0 0% 100%',
      muted: '150 20% 95%',
      mutedForeground: '150 10% 40%',
      accent: '84 81% 44%',
      accentForeground: '0 0% 100%',
      destructive: '0 84.2% 60.2%',
      destructiveForeground: '0 0% 100%',
      border: '150 15% 88%',
      input: '150 15% 88%',
      ring: '160 84% 39%',
      sidebarBackground: '150 20% 97%',
      sidebarForeground: '150 30% 20%',
      sidebarPrimary: '160 84% 39%',
      sidebarPrimaryForeground: '0 0% 100%',
      sidebarAccent: '150 20% 93%',
      sidebarAccentForeground: '150 30% 15%',
      sidebarBorder: '150 15% 86%',
      sidebarRing: '160 84% 39%',
    },
    dark: {
      background: '150 30% 5%',
      foreground: '150 20% 95%',
      card: '150 25% 8%',
      cardForeground: '150 20% 95%',
      popover: '150 25% 8%',
      popoverForeground: '150 20% 95%',
      primary: '160 84% 45%',
      primaryForeground: '150 30% 5%',
      secondary: '25 5% 30%',
      secondaryForeground: '150 20% 95%',
      muted: '150 20% 15%',
      mutedForeground: '150 10% 55%',
      accent: '84 81% 50%',
      accentForeground: '150 30% 5%',
      destructive: '0 62.8% 35%',
      destructiveForeground: '150 20% 95%',
      border: '150 20% 18%',
      input: '150 20% 18%',
      ring: '160 84% 45%',
      sidebarBackground: '150 28% 7%',
      sidebarForeground: '150 20% 90%',
      sidebarPrimary: '160 84% 45%',
      sidebarPrimaryForeground: '150 30% 5%',
      sidebarAccent: '150 20% 12%',
      sidebarAccentForeground: '150 20% 90%',
      sidebarBorder: '150 20% 15%',
      sidebarRing: '160 84% 45%',
    },
  },

  [COLOR_SCHEMES.SUNSET]: {
    id: 'sunset',
    name: 'Sunset',
    description: 'Warm orange and amber tones',
    preview: {
      primary: '#F97316',
      secondary: '#78716C',
      accent: '#F59E0B',
    },
    light: {
      background: '0 0% 100%',
      foreground: '20 30% 10%',
      card: '0 0% 100%',
      cardForeground: '20 30% 10%',
      popover: '0 0% 100%',
      popoverForeground: '20 30% 10%',
      primary: '25 95% 53%',
      primaryForeground: '0 0% 100%',
      secondary: '25 5% 45%',
      secondaryForeground: '0 0% 100%',
      muted: '30 20% 95%',
      mutedForeground: '20 10% 40%',
      accent: '38 92% 50%',
      accentForeground: '0 0% 100%',
      destructive: '0 84.2% 60.2%',
      destructiveForeground: '0 0% 100%',
      border: '30 15% 88%',
      input: '30 15% 88%',
      ring: '25 95% 53%',
      sidebarBackground: '30 25% 97%',
      sidebarForeground: '20 30% 20%',
      sidebarPrimary: '25 95% 53%',
      sidebarPrimaryForeground: '0 0% 100%',
      sidebarAccent: '30 25% 93%',
      sidebarAccentForeground: '20 30% 15%',
      sidebarBorder: '30 15% 86%',
      sidebarRing: '25 95% 53%',
    },
    dark: {
      background: '20 30% 5%',
      foreground: '30 20% 95%',
      card: '20 25% 8%',
      cardForeground: '30 20% 95%',
      popover: '20 25% 8%',
      popoverForeground: '30 20% 95%',
      primary: '25 95% 58%',
      primaryForeground: '20 30% 5%',
      secondary: '25 5% 30%',
      secondaryForeground: '30 20% 95%',
      muted: '20 20% 15%',
      mutedForeground: '20 10% 55%',
      accent: '38 92% 55%',
      accentForeground: '20 30% 5%',
      destructive: '0 62.8% 35%',
      destructiveForeground: '30 20% 95%',
      border: '20 20% 18%',
      input: '20 20% 18%',
      ring: '25 95% 58%',
      sidebarBackground: '20 28% 7%',
      sidebarForeground: '30 20% 90%',
      sidebarPrimary: '25 95% 58%',
      sidebarPrimaryForeground: '20 30% 5%',
      sidebarAccent: '20 20% 12%',
      sidebarAccentForeground: '30 20% 90%',
      sidebarBorder: '20 20% 15%',
      sidebarRing: '25 95% 58%',
    },
  },

  [COLOR_SCHEMES.ROYAL]: {
    id: 'royal',
    name: 'Royal',
    description: 'Elegant indigo and purple tones',
    preview: {
      primary: '#6366F1',
      secondary: '#71717A',
      accent: '#A855F7',
    },
    light: {
      background: '0 0% 100%',
      foreground: '240 30% 10%',
      card: '0 0% 100%',
      cardForeground: '240 30% 10%',
      popover: '0 0% 100%',
      popoverForeground: '240 30% 10%',
      primary: '239 84% 67%',
      primaryForeground: '0 0% 100%',
      secondary: '240 4% 46%',
      secondaryForeground: '0 0% 100%',
      muted: '240 10% 95%',
      mutedForeground: '240 5% 40%',
      accent: '271 81% 56%',
      accentForeground: '0 0% 100%',
      destructive: '0 84.2% 60.2%',
      destructiveForeground: '0 0% 100%',
      border: '240 10% 88%',
      input: '240 10% 88%',
      ring: '239 84% 67%',
      sidebarBackground: '240 15% 97%',
      sidebarForeground: '240 30% 20%',
      sidebarPrimary: '239 84% 67%',
      sidebarPrimaryForeground: '0 0% 100%',
      sidebarAccent: '240 15% 93%',
      sidebarAccentForeground: '240 30% 15%',
      sidebarBorder: '240 10% 86%',
      sidebarRing: '239 84% 67%',
    },
    dark: {
      background: '240 30% 5%',
      foreground: '240 10% 95%',
      card: '240 25% 8%',
      cardForeground: '240 10% 95%',
      popover: '240 25% 8%',
      popoverForeground: '240 10% 95%',
      primary: '239 84% 72%',
      primaryForeground: '240 30% 5%',
      secondary: '240 4% 30%',
      secondaryForeground: '240 10% 95%',
      muted: '240 15% 15%',
      mutedForeground: '240 5% 55%',
      accent: '271 81% 62%',
      accentForeground: '240 30% 5%',
      destructive: '0 62.8% 35%',
      destructiveForeground: '240 10% 95%',
      border: '240 15% 18%',
      input: '240 15% 18%',
      ring: '239 84% 72%',
      sidebarBackground: '240 28% 7%',
      sidebarForeground: '240 10% 90%',
      sidebarPrimary: '239 84% 72%',
      sidebarPrimaryForeground: '240 30% 5%',
      sidebarAccent: '240 15% 12%',
      sidebarAccentForeground: '240 10% 90%',
      sidebarBorder: '240 15% 15%',
      sidebarRing: '239 84% 72%',
    },
  },
};

export const getAvailableColorSchemes = () => Object.values(colorSchemes);

export const getColorSchemeById = (schemeId) => 
  colorSchemes[schemeId] || colorSchemes[COLOR_SCHEMES.DEFAULT];

export const isValidColorScheme = (schemeId) => 
  Object.values(COLOR_SCHEMES).includes(schemeId);

/**
 * Get color values for a specific scheme and mode
 * @param {string} schemeId - Color scheme ID
 * @param {string} mode - 'light' or 'dark'
 * @returns {object} Color values for the scheme and mode
 */
export const getColorValues = (schemeId, mode) => {
  const scheme = getColorSchemeById(schemeId);
  return scheme[mode] || scheme.light;
};
